<template>
  <div>
    <div class="w-80">
      <NLoading v-if="isLoadingSymbol" class="text-brand-primary" size="10" />

      <div v-else class="p-3">
        <div class="flex justify-between gap-1">
          <div class="flex items-center">
            <SymbolTradeStateIcon
              class="me-2"
              size="medium"
              :trading-state-code="symbol.instrumentTradingState"
            />

            <Retail :isin="getSymbolIsin(symbol)">
              <NText variant="h3" tag="h3" weight="bold">
                {{ symbol.instrumentName }}
              </NText>
            </Retail>
          </div>

          <div class="flex flex-col justify-around">
            <div class="inline-flex items-center">
              <NumberFormatter
                #="{ number }"
                :value="(symbol.referencePriceVariationPercentage ?? 0) / 100"
                maximum-fraction-digits="2"
                sign-display="always"
                number-style="percent"
              >
                <NText
                  weight="bold"
                  dir="ltr"
                  class="mx-1"
                  :class="{
                    'text-brand-primary':
                      (symbol.referencePriceVariationPercentage ?? 0) > 0,
                    'text-critical-primary':
                      (symbol.referencePriceVariationPercentage ?? 0) < 0,
                  }"
                >
                  ({{ number }})
                </NText>
              </NumberFormatter>

              <NumberFormatter #="{ number }" :value="symbol.lastTradePrice">
                <ChangePriceEffect :price="symbol.lastTradePrice ?? 0">
                  <NText :key="number" variant="h1" tag="h1" weight="bold">
                    {{ number }}
                  </NText>
                </ChangePriceEffect>
              </NumberFormatter>
            </div>
          </div>
        </div>

        <div>
          <NText variant="caption1" class="text-neutral-secondary">
            {{ symbol.instrumentTitle }}
          </NText>
        </div>
      </div>
    </div>

    <div class="mt-2 p-4 bg-neutral-secondary rounded-md">
      <div class="flex gap-2 flex-col">
        <div class="flex justify-between items-center">
          <NText variant="caption1" weight="medium">
            {{ t('orders.orderType') }}
          </NText>

          <NText variant="caption1" weight="medium" class="pr-2">
            {{ t('orders.conditional.title') }}
          </NText>
        </div>

        <div class="flex justify-between items-center">
          <NText variant="caption1" weight="medium">
            {{ t('orders.orderSide') }}
          </NText>

          <NText
            v-if="selectedOrder.orderSideId === OrderSideEnum.Buy"
            variant="caption1"
            weight="medium"
            class="pr-2 text-brand-primary"
          >
            {{ t('common.buy') }}
          </NText>

          <NText
            v-else
            variant="caption1"
            weight="medium"
            class="pr-2 text-critical-primary"
          >
            {{ t('common.sell') }}
          </NText>
        </div>

        <div class="flex justify-between items-center">
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.order_state') }}
          </NText>

          <NText variant="caption1" weight="medium">
            {{ selectedOrder.orderState }}
          </NText>
        </div>
      </div>

      <div class="border-b border-neutral-tertiary w-full my-3" />

      <div class="flex gap-2 flex-col">
        <div v-if="isInvestOrder" class="flex justify-between items-center">
          <NText variant="caption1" weight="medium">
            {{
              isConditionalOrderDone
                ? t('misc.quantity')
                : t('modules.order.approximateOrderQuantity')
            }}
          </NText>

          <NLoading
            v-if="isLoadingSymbol"
            class="text-brand-primary"
            size="10"
          />

          <div v-else>
            <NumberFormatter
              #="{ number }"
              :value="
                getConditionalOrderQuantity(
                  selectedOrder,
                  wage,
                  isConditionalOrderDone
                )
              "
              round
            >
              <NText
                :key="number"
                variant="caption1"
                weight="medium"
                class="mx-1"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </div>

        <div v-else class="flex justify-between items-center">
          <NText variant="caption1" weight="medium">
            {{ t('misc.quantity') }}
            ({{ t('modules.order.executed') }})
          </NText>

          <div class="flex flex-row items-center">
            <NumberFormatter
              #="{ number }"
              :value="selectedOrder.executedQuantity"
            >
              <NText variant="caption1" weight="medium" class="pl-1">
                ({{ number }})
              </NText>
            </NumberFormatter>

            <NumberFormatter #="{ number }" :value="selectedOrder.quantity">
              <NText variant="caption1" weight="medium">{{ number }}</NText>
            </NumberFormatter>
          </div>
        </div>

        <div class="flex justify-between items-center">
          <NText variant="caption1" weight="medium">
            {{ t('common.priceCondition') }}
            {{
              selectedOrder.upperThreshold
                ? t('common.lowerThan')
                : t('common.upperThan')
            }}
          </NText>

          <div>
            <NumberFormatter
              #="{ number }"
              :value="getOrderPrice(selectedOrder)"
            >
              <NText
                :key="number"
                variant="caption1"
                weight="medium"
                class="mx-1"
              >
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <div
          v-if="algorithmAverageTransactionPrice(props.selectedOrder, wage)"
          class="flex justify-between items-center"
        >
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.AverageTransactionPrice') }}
          </NText>

          <div class="flex flex-row items-center">
            <NumberFormatter
              #="{ number }"
              :value="
                algorithmAverageTransactionPrice(props.selectedOrder, wage)
              "
              round
            >
              <NText variant="caption1" weight="medium" class="pl-1">
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <template v-if="isInvestOrder">
          <div class="flex justify-between items-center">
            <NText variant="caption1" weight="medium">
              {{ t('modules.order.order_value') }}
            </NText>

            <div class="flex flex-row items-center">
              <NumberFormatter
                #="{ number }"
                :value="selectedOrder.investValue"
              >
                <NText variant="caption1" class="pe-1" weight="medium">
                  {{ number }}
                </NText>
              </NumberFormatter>

              <NText variant="overline" class="text-neutral-secondary">
                {{ t('misc.rial') }}
              </NText>
            </div>
          </div>

          <div class="flex justify-between items-center">
            <NText variant="caption1" weight="medium">
              {{ t('modules.order.executedValue') }}
            </NText>

            <div class="flex flex-row items-center">
              <NumberFormatter
                #="{ number }"
                :value="selectedOrder.executedValue"
              >
                <NText variant="caption1" weight="medium" class="pe-1">
                  {{ number }}
                </NText>
              </NumberFormatter>

              <NText variant="overline" class="text-neutral-secondary">
                {{ t('misc.rial') }}
              </NText>
            </div>
          </div>
        </template>

        <div v-else class="flex justify-between items-center">
          <NText variant="caption1" weight="medium">
            {{
              isConditionalOrderDone
                ? t('modules.order.order_value')
                : t('modules.order.approximateOrderValue')
            }}
          </NText>

          <NLoading
            v-if="isLoadingSymbol"
            class="text-brand-primary"
            size="10"
          />

          <div v-else>
            <NumberFormatter
              #="{ number }"
              :value="
                getConditionalOrderValue(
                  selectedOrder,
                  wage,
                  isConditionalOrderDone
                )
              "
              round
            >
              <NText
                :key="number"
                variant="caption1"
                weight="medium"
                class="mx-1"
              >
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>
      </div>

      <div class="border-b border-neutral-tertiary w-full my-3" />

      <div class="flex gap-2 flex-col">
        <div class="flex justify-between items-center">
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.creationTime') }}
          </NText>

          <div>
            <NText variant="caption1" weight="medium" class="pr-2">
              {{ creationTime }}
            </NText>
          </div>
        </div>

        <div class="flex justify-between items-center">
          <NText variant="caption1" weight="medium">
            {{ t('orders.valid_until') }}
          </NText>

          <NText variant="caption1" weight="medium">
            {{ orderValidity[selectedOrder.validityTypeId] }}
          </NText>
        </div>
      </div>
    </div>

    <div v-if="isDeletable" class="flex items-center mt-6">
      <NButton
        v-gid="{
          module: 'order',
          screen: 'home',
          section: 'conditionalOrderDetailDialog',
          component: 'orderCancelButton',
        }"
        variant="outline"
        color="red"
        class="w-full"
        size="small"
        @click="handleDeleteOrder($event.target)"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.cancelOrder') }}
        </NText>
      </NButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '@negar/core';
import { OrderItem, OrderSideEnum } from '@libs/api-reference';
import { sleep } from '@libs/utils';
import { useQueryClient } from '@libs/use';
import {
  VALIDITY_TYPES,
  getOrderPrice,
  getConditionalOrderValue,
  getConditionalOrderQuantity,
  useDeleteOfflineOrderQuery,
  isDoneOrder,
  ORDERS_QUERY_KEYS,
  isDeletableOrder,
  algorithmAverageTransactionPrice,
} from '@core/order';
import { getSymbolIsin, useSymbolDeductions } from '@core/symbol';

import { useOrderDialogs } from '@/modules/order';
import {
  Retail,
  SymbolTradeStateIcon,
  useLiveSymbolQuery,
} from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import { useRoute } from 'vue-router';

type ConditionalOrderDetailProps = {
  selectedOrder: OrderItem;
};

const props = defineProps<ConditionalOrderDetailProps>();

const { t, d } = useI18n();
const route = useRoute();

const toast = useToast();

const queryClient = useQueryClient();
const { findWageConfigBySymbol } = useSymbolDeductions();
const { openDeleteOfflineOrderDialog } = useOrderDialogs();

const isin = ref(props.selectedOrder?.instrumentIdentification as string);

const wage = findWageConfigBySymbol(props.selectedOrder);

const { symbol, isLoadingSymbol } = useLiveSymbolQuery(isin);

const { deleteOfflineOrder, isLoadingDeleteOfflineOrder } =
  useDeleteOfflineOrderQuery({
    async onSuccess(result, { closeDeleteOrderDialog }) {
      toast.neutral({
        message: t('modules.order.cancelOrderMessage'),
        duration: 3000,
      });

      await sleep(1000);

      queryClient.invalidateQueries(ORDERS_QUERY_KEYS.orders);

      closeDeleteOrderDialog();
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });

function handleDeleteOrder(deleteOrderButtonRef: HTMLElement) {
  openDeleteOfflineOrderDialog({
    handleDelete: deleteOfflineOrder,
    loading: isLoadingDeleteOfflineOrder.value,
    order: props.selectedOrder,
    referenceElementRef: deleteOrderButtonRef,
  });
}

const isInvestOrder = Boolean(props.selectedOrder.investValue);

const validityDateTitle = computed(() => {
  const creationDay = new Date(props.selectedOrder.entryDate).getDate();
  const today = new Date().getDate();

  return creationDay === today
    ? t('misc.today')
    : d(props.selectedOrder.entryDate, 'shortDate');
});

const creationTime = computed(
  () => `${d(props.selectedOrder.entryDate, 'shortDate')} ,
    ${d(props.selectedOrder.entryDate, 'shortTime')}`
);

const orderValidity = computed(() => ({
  [VALIDITY_TYPES.Day]: validityDateTitle.value,
  [VALIDITY_TYPES.GoodTillCancelled]: t('common.cancellation'),
  [VALIDITY_TYPES.GoodTillDate]: props.selectedOrder.validityDate
    ? d(props.selectedOrder.validityDate, 'shortDate')
    : '',
}));

const isFromHistoryPath = route.path.includes('history');

const isDeletable = computed(() =>
  isDeletableOrder(props.selectedOrder, isFromHistoryPath)
);

const isConditionalOrderDone = computed(() => {
  if (isFromHistoryPath && 'newOrderState' in props.selectedOrder) {
    return isDoneOrder(
      props.selectedOrder,
      Boolean(props.selectedOrder.newOrderState)
    );
  }

  return isDoneOrder(props.selectedOrder);
});
</script>
